var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import { BindingError } from "../BindingError.js";
import { RealmKeyGenerator } from "./RealmKeyGenerator.js";
import { TreeParameterMerger } from "./TreeParameterMerger.js";
import { FieldOperations } from "./operations/FieldOperations.js";
import { EntityOperations } from "./operations/EntityOperations.js";
import { ListOperations } from "./operations/ListOperations.js";
import { EntityAccessor } from "../accessors/EntityAccessor.js";
import { BijectiveIndexedMap } from "../structures/BijectiveIndexedMap.js";
import { EntityListAccessor } from "../accessors/EntityListAccessor.js";
import { EntityListSubTreeMarker } from "../markers/EntityListSubTreeMarker.js";
import { ServerId, UnpersistedEntityDummyId } from "../accessorTree/RuntimeId.js";
import { getEntityMarker } from "./state/getEntityMarker.js";
import { FieldMarker } from "../markers/FieldMarker.js";
import { HasManyRelationMarker } from "../markers/HasManyRelationMarker.js";
import { HasOneRelationMarker } from "../markers/HasOneRelationMarker.js";
import { assertNever } from "../utils/assertNever.js";
import { FieldAccessor } from "../accessors/FieldAccessor.js";
import { EventListenersStore } from "../treeParameters/EventListenersStore.js";
class StateInitializer {
  constructor(accessorErrorManager, batchUpdatesOptions, eventManager, treeStore) {
    __publicField(this, "fieldOperations");
    __publicField(this, "entityOperations");
    __publicField(this, "listOperations");
    this.eventManager = eventManager;
    this.treeStore = treeStore;
    this.fieldOperations = new FieldOperations(accessorErrorManager, this.eventManager, this, this.treeStore);
    this.entityOperations = new EntityOperations(accessorErrorManager, batchUpdatesOptions, this.eventManager, this, this.treeStore);
    this.listOperations = new ListOperations(accessorErrorManager, batchUpdatesOptions, this.eventManager, this, this.treeStore);
  }
  initializeSubTree(tree) {
    let subTreeState;
    const persistedRootData = this.treeStore.subTreePersistedData.get(tree.placeholderName);
    if (tree instanceof EntityListSubTreeMarker) {
      const persistedEntityIds = persistedRootData instanceof Set ? persistedRootData : /* @__PURE__ */ new Set();
      subTreeState = this.initializeEntityListState(
        { marker: tree, parent: void 0 },
        tree.entityName,
        persistedEntityIds
      );
    } else {
      subTreeState = this.materializeEntityRealm(
        this.initializeEntityRealm(
          persistedRootData instanceof ServerId ? persistedRootData : new UnpersistedEntityDummyId(),
          tree.entityName,
          {
            type: "subTree",
            marker: tree,
            parent: void 0
          }
        )
      );
    }
    return subTreeState;
  }
  initializeEntityRealm(id, entityName, blueprint, copyFrom) {
    const entity = this.initializeEntityState(id, entityName);
    const realmKey = RealmKeyGenerator.getRealmKey(id, blueprint);
    const existing = this.treeStore.entityRealmStore.get(realmKey);
    if (existing !== void 0) {
      return existing;
    }
    if (!copyFrom || copyFrom.type !== "entityRealm") {
      const stub = {
        type: "entityRealmStub",
        blueprint,
        entity,
        realmKey,
        getAccessor: () => this.materializeEntityRealm(stub).getAccessor()
      };
      this.registerEntityRealm(stub);
      return stub;
    }
    const realm = this.createEntityRealm(blueprint, realmKey, entity, copyFrom);
    this.registerEntityRealm(realm);
    const marker = getEntityMarker(realm);
    const pathBack = this.treeStore.getPathBackToParent(realm);
    for (const [placeholderName, field] of marker.fields.markers) {
      if (field instanceof FieldMarker) {
        const value = copyFrom.children.get(placeholderName);
        if (!value || value.type !== "field") {
          throw new BindingError();
        }
        this.initializeFromFieldMarker(realm, field, void 0, value);
      } else if (field instanceof HasManyRelationMarker) {
        const value = copyFrom.children.get(placeholderName);
        if (!value || value.type !== "entityList") {
          throw new BindingError();
        }
        this.initializeFromHasManyRelationMarker(realm, field, new Set(value.children.keys()));
      } else if (field instanceof HasOneRelationMarker) {
        let runtimeId;
        let subCopyFrom = void 0;
        if (pathBack?.fieldBackToParent === field.parameters.field) {
          runtimeId = pathBack.parent.entity.id;
        } else {
          const value = copyFrom.children.get(placeholderName);
          if (!value || value.type !== "entityRealm" && value.type !== "entityRealmStub") {
            throw new BindingError();
          }
          runtimeId = !(value.entity.id instanceof UnpersistedEntityDummyId) ? value.entity.id : new UnpersistedEntityDummyId();
          if (value.type === "entityRealm") {
            subCopyFrom = value;
          }
        }
        this.initializeFromHasOneRelationMarker(realm, field, runtimeId, subCopyFrom);
      } else {
        assertNever();
      }
    }
    this.eventManager.registerNewlyInitialized(realm);
    return realm;
  }
  createEntityRealm(blueprint, realmKey, entity, copyFrom) {
    const entityRealm = {
      type: "entityRealm",
      blueprint,
      realmKey,
      entity,
      accessor: void 0,
      children: /* @__PURE__ */ new Map(),
      childrenWithPendingUpdates: void 0,
      errors: void 0,
      eventListeners: this.initializeEntityEventListenerStore(blueprint),
      fieldsWithPendingConnectionUpdates: void 0,
      plannedHasOneDeletions: void 0,
      unpersistedChangesCount: copyFrom?.unpersistedChangesCount ?? 0,
      getAccessor: () => {
        if (entityRealm.accessor === void 0) {
          const entity2 = entityRealm.entity;
          entityRealm.accessor = new EntityAccessor(
            entityRealm,
            this.entityOperations,
            entity2.id,
            entityRealm.realmKey,
            entity2.entityName,
            // We're technically exposing more info in runtime than we'd like but that way we don't have to allocate and
            // keep in sync two copies of the same data. TS hides the extra info anyway.
            entityRealm.children,
            this.treeStore.persistedEntityData.get(entity2.id.uniqueValue),
            entityRealm.unpersistedChangesCount !== 0,
            entityRealm.errors,
            getEntityMarker(entityRealm).environment,
            entityRealm.getAccessor
          );
        }
        return entityRealm.accessor;
      }
    };
    return entityRealm;
  }
  materializeEntityRealm(state) {
    if (state.type === "entityRealm") {
      return state;
    }
    const { realmKey, entity, blueprint } = state;
    const existing = this.treeStore.entityRealmStore.get(realmKey);
    if (existing !== void 0 && existing.type === "entityRealm") {
      return existing;
    }
    const entityRealm = this.createEntityRealm(blueprint, realmKey, entity);
    this.registerEntityRealm(entityRealm);
    const persistedData = this.treeStore.persistedEntityData.get(entity.id.uniqueValue);
    const marker = getEntityMarker(state);
    const pathBack = this.treeStore.getPathBackToParent(entityRealm);
    for (const [placeholderName, field] of marker.fields.markers) {
      const fieldDatum = persistedData?.get(placeholderName);
      if (field instanceof FieldMarker) {
        this.initializeFromFieldMarker(entityRealm, field, fieldDatum);
      } else if (field instanceof HasManyRelationMarker) {
        this.initializeFromHasManyRelationMarker(entityRealm, field, fieldDatum instanceof Set ? fieldDatum : /* @__PURE__ */ new Set());
      } else if (field instanceof HasOneRelationMarker) {
        let runtimeId;
        if (pathBack?.fieldBackToParent === field.parameters.field) {
          runtimeId = pathBack.parent.entity.id;
        } else {
          runtimeId = fieldDatum instanceof ServerId ? fieldDatum : new UnpersistedEntityDummyId();
        }
        this.initializeFromHasOneRelationMarker(entityRealm, field, runtimeId);
      } else {
        assertNever();
      }
    }
    this.eventManager.registerNewlyInitialized(entityRealm);
    return entityRealm;
  }
  registerEntityRealm(entityRealm) {
    const { realmKey, blueprint, entity } = entityRealm;
    this.treeStore.entityRealmStore.set(entityRealm.realmKey, entityRealm);
    if (blueprint.type === "listEntity") {
      blueprint.parent.children.set(entity.id.value, entityRealm);
    } else if (blueprint.type === "hasOne") {
      blueprint.parent.children.set(blueprint.marker.placeholderName, entityRealm);
    }
    entity.realms.set(realmKey, entityRealm);
  }
  initializeEntityState(id, entityName) {
    const entityId = id.uniqueValue;
    const existing = this.treeStore.entityStore.get(entityId);
    if (existing !== void 0) {
      return existing;
    }
    const entityState = {
      entityName,
      hasIdSetInStone: id instanceof ServerId,
      id,
      isScheduledForDeletion: false,
      maidenId: id instanceof UnpersistedEntityDummyId ? id : void 0,
      realms: /* @__PURE__ */ new Map()
    };
    this.treeStore.entityStore.set(entityId, entityState);
    return entityState;
  }
  initializeEntityListState(blueprint, entityName, initialEntityIds) {
    const entityListState = {
      type: "entityList",
      accessor: void 0,
      blueprint,
      children: new BijectiveIndexedMap((realm) => realm.entity.id.value),
      childrenWithPendingUpdates: void 0,
      entityName,
      eventListeners: this.initializeEntityListEventListenerStore(blueprint),
      childEventListeners: this.initializeEntityListChildEventListenerStore(blueprint),
      errors: void 0,
      plannedRemovals: void 0,
      unpersistedChangesCount: 0,
      // TODO force creation?
      getAccessor: () => {
        if (entityListState.accessor === void 0) {
          const persistedEntityIds = this.treeStore.getEntityListPersistedIds(entityListState);
          entityListState.accessor = new EntityListAccessor(
            entityListState,
            this.listOperations,
            entityListState.children,
            persistedEntityIds,
            entityListState.unpersistedChangesCount !== 0,
            entityListState.errors,
            entityListState.blueprint.marker.environment,
            entityListState.getAccessor
          );
        }
        return entityListState.accessor;
      }
    };
    const initialData = initialEntityIds.size === 0 ? Array.from({ length: blueprint.marker.parameters.initialEntityCount }) : initialEntityIds;
    for (const entityId of initialData) {
      this.initializeEntityRealm(
        entityId ? new ServerId(entityId, entityName) : new UnpersistedEntityDummyId(),
        entityListState.entityName,
        {
          type: "listEntity",
          parent: entityListState
        }
      );
    }
    this.eventManager.registerNewlyInitialized(entityListState);
    return entityListState;
  }
  initializeFieldState(parent, placeholderName, fieldMarker, persistedValue, copyFromState) {
    const resolvedFieldValue = copyFromState ? copyFromState.value : persistedValue ?? fieldMarker.defaultValue ?? null;
    const fieldState = {
      type: "field",
      accessor: void 0,
      fieldMarker,
      placeholderName,
      persistedValue,
      parent,
      value: resolvedFieldValue,
      eventListeners: this.initializeFieldEventListenerStore(fieldMarker),
      errors: void 0,
      touchLog: void 0,
      hasUnpersistedChanges: copyFromState?.hasUnpersistedChanges ?? false,
      getAccessor: () => {
        if (fieldState.accessor === void 0) {
          const fieldSchema = this.treeStore.schema.getEntityColumn(parent.entity.entityName, fieldMarker.fieldName);
          fieldState.accessor = new FieldAccessor(
            fieldState,
            this.fieldOperations,
            fieldState.placeholderName,
            fieldState.value,
            fieldState.persistedValue === void 0 ? null : fieldState.persistedValue,
            fieldState.fieldMarker.defaultValue,
            fieldState.errors,
            fieldState.hasUnpersistedChanges,
            fieldState.touchLog,
            fieldState.getAccessor,
            fieldSchema
          );
        }
        return fieldState.accessor;
      }
    };
    this.eventManager.registerNewlyInitialized(fieldState);
    return fieldState;
  }
  initializeFromFieldMarker(parent, field, persistedValue, copyFromState) {
    if (persistedValue instanceof Set) {
      throw new BindingError(
        `Received a collection of referenced entities where a single '${field.fieldName}' field was expected. Perhaps you wanted to use a <Repeater />?`
      );
    }
    if (persistedValue instanceof ServerId) {
      throw new BindingError(
        `Received a referenced entity where a single '${field.fieldName}' field was expected. Perhaps you wanted to use <HasOne />?`
      );
    }
    parent.children.set(
      field.placeholderName,
      this.initializeFieldState(parent, field.placeholderName, field, persistedValue, copyFromState)
    );
  }
  initializeFromHasOneRelationMarker(parent, field, entityId, copyFrom) {
    parent.children.set(
      field.placeholderName,
      this.initializeEntityRealm(entityId, this.getRelationTargetEntityName(parent, field), {
        type: "hasOne",
        parent,
        marker: field
      }, copyFrom)
    );
  }
  initializeFromHasManyRelationMarker(parent, field, persistedEntityIds) {
    parent.children.set(
      field.placeholderName,
      this.initializeEntityListState(
        { marker: field, parent },
        this.getRelationTargetEntityName(parent, field),
        persistedEntityIds
      )
    );
  }
  getRelationTargetEntityName(entityRealm, field) {
    const targetField = this.treeStore.schema.getEntityRelation(entityRealm.entity.entityName, field.parameters.field);
    return targetField.targetEntity;
  }
  runImmediateUserInitialization(realm, initialize) {
    if (initialize === void 0) {
      return;
    }
    const entityRealm = this.materializeEntityRealm(realm);
    realm.getAccessor().batchUpdates(initialize);
    const initializeListeners = this.eventManager.getEventListeners(entityRealm, { type: "initialize" });
    if (initializeListeners === void 0 || initializeListeners.size === 0) {
      realm.entity.hasIdSetInStone = true;
    }
  }
  initializeEntityEventListenerStore(blueprint) {
    if (blueprint.type === "listEntity") {
      const blueprintListeners2 = blueprint.parent.blueprint.marker.parameters.childEventListeners;
      const childInitialize = blueprintListeners2?.get({ type: "initialize" });
      const store = new EventListenersStore(() => blueprint.parent.childEventListeners);
      if (childInitialize !== void 0) {
        store.set({ type: "initialize" }, childInitialize);
      }
      return store;
    }
    const blueprintListeners = blueprint.marker.parameters.eventListeners;
    if (blueprintListeners === void 0) {
      return void 0;
    }
    return TreeParameterMerger.cloneSingleEntityEventListeners(blueprintListeners);
  }
  initializeEntityListEventListenerStore(blueprint) {
    const blueprintListeners = blueprint.marker.parameters.eventListeners;
    if (blueprintListeners === void 0) {
      return void 0;
    }
    return TreeParameterMerger.cloneEntityListEventListeners(blueprintListeners);
  }
  initializeEntityListChildEventListenerStore(blueprint) {
    const blueprintListeners = blueprint.marker.parameters.childEventListeners;
    if (blueprintListeners === void 0) {
      return void 0;
    }
    return TreeParameterMerger.cloneSingleEntityEventListeners(blueprintListeners);
  }
  initializeFieldEventListenerStore(marker) {
    return void 0;
  }
}
export {
  StateInitializer
};
//# sourceMappingURL=StateInitializer.js.map
